import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { md5 } from './md5';

/**
 * Generate a unique session ID
 * @returns {string} A unique session identifier
 */
export function generateUniqueId() {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 15);
  return `session_${timestamp}_${randomPart}`;
}

/**
 * Generate a consistent session ID based on email address
 * This ensures the same person always gets the same session ID
 * @param {string} email - The user's email address
 * @returns {string} A consistent session identifier for this email
 */
export function generateConsistentSessionId(email) {
  if (!email) {
    return generateUniqueId(); // Fallback to random if no email
  }
  const emailLower = email.toLowerCase().trim();
  const hash = md5(emailLower);
  const shortHash = hash.substring(0, 8);
  return `user_${shortHash}`;
}

/**
 * Create a new user session
 * @param {string} uniqueId - The unique session ID
 * @param {Object} inviteData - Optional invite data if coming from an invite
 * @returns {Promise<Object>} The created session data
 */
export async function createUserSession(uniqueId, inviteData = null) {
  try {
    const sessionData = {
      uniqueId,
      status: 'active',
      step: 1,
      completed: false,
      createdAt: serverTimestamp(),
      lastAccessed: serverTimestamp(),
      inviteData: inviteData || null,
      formData: {},
      completedAt: null
    };

    await setDoc(doc(db, 'userSessions', uniqueId), sessionData);
    console.log('[SessionTracker] Created new session:', uniqueId);
    return sessionData;
  } catch (error) {
    console.error('[SessionTracker] Error creating session:', error);
    throw error;
  }
}

/**
 * Get user session data
 * @param {string} uniqueId - The unique session ID
 * @returns {Promise<Object|null>} The session data or null if not found
 */
export async function getUserSession(uniqueId) {
  try {
    const sessionDoc = await getDoc(doc(db, 'userSessions', uniqueId));
    if (sessionDoc.exists()) {
      const sessionData = sessionDoc.data();
      // Update last accessed time
      await updateDoc(doc(db, 'userSessions', uniqueId), {
        lastAccessed: serverTimestamp()
      });
      return sessionData;
    }
    return null;
  } catch (error) {
    console.error('[SessionTracker] Error getting session:', error);
    return null;
  }
}

/**
 * Update user session data
 * @param {string} uniqueId - The unique session ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<void>}
 */
export async function updateUserSession(uniqueId, updateData) {
  try {
    await updateDoc(doc(db, 'userSessions', uniqueId), {
      ...updateData,
      lastAccessed: serverTimestamp()
    });
    console.log('[SessionTracker] Updated session:', uniqueId);
  } catch (error) {
    console.error('[SessionTracker] Error updating session:', error);
    throw error;
  }
}

/**
 * Mark session as completed
 * @param {string} uniqueId - The unique session ID
 * @param {Object} formData - The completed form data
 * @returns {Promise<void>}
 */
export async function completeUserSession(uniqueId, formData) {
  try {
    await updateDoc(doc(db, 'userSessions', uniqueId), {
      completed: true,
      completedAt: serverTimestamp(),
      formData: formData,
      status: 'completed',
      lastAccessed: serverTimestamp()
    });
    console.log('[SessionTracker] Completed session:', uniqueId);
  } catch (error) {
    console.error('[SessionTracker] Error completing session:', error);
    throw error;
  }
}

/**
 * Check if a session is valid and accessible
 * @param {string} uniqueId - The unique session ID
 * @returns {Promise<boolean>} True if session is valid
 */
export async function isSessionValid(uniqueId) {
  try {
    const session = await getUserSession(uniqueId);
    return session !== null;
  } catch (error) {
    console.error('[SessionTracker] Error checking session validity:', error);
    return false;
  }
}

/**
 * Get the current step for a session
 * @param {string} uniqueId - The unique session ID
 * @returns {Promise<number>} The current step (1, 2, or 3)
 */
export async function getSessionStep(uniqueId) {
  try {
    const session = await getUserSession(uniqueId);
    if (!session) return 1;
    
    // If completed, always show step 3 (welcome message)
    if (session.completed) return 3;
    
    return session.step || 1;
  } catch (error) {
    console.error('[SessionTracker] Error getting session step:', error);
    return 1;
  }
}

